import React from 'react';
import { Check, X } from 'lucide-react';

/**
 * Password Requirements Component
 * Shows password strength requirements with real-time validation feedback
 */
const PasswordRequirements = ({ password, showRequirements = true }) => {
  const requirements = [
    {
      id: 'length',
      label: 'At least 8 characters',
      test: (pwd) => pwd.length >= 8
    },
    {
      id: 'uppercase',
      label: 'One uppercase letter (A-Z)',
      test: (pwd) => /[A-Z]/.test(pwd)
    },
    {
      id: 'lowercase',
      label: 'One lowercase letter (a-z)',
      test: (pwd) => /[a-z]/.test(pwd)
    },
    {
      id: 'number',
      label: 'One number (0-9)',
      test: (pwd) => /\d/.test(pwd)
    },
    {
      id: 'special',
      label: 'One special character (!@#$%^&*)',
      test: (pwd) => /[!@#$%^&*(),.?":{}|<>]/.test(pwd)
    }
  ];

  if (!showRequirements) return null;

  return (
    <div className="mt-2 p-3 bg-[#0F1923] rounded-lg border border-[#2D3748]">
      <p className="text-xs font-semibold text-slate-300 mb-2">Password must contain:</p>
      <ul className="space-y-1">
        {requirements.map((req) => {
          const isMet = req.test(password);
          return (
            <li key={req.id} className="flex items-center space-x-2 text-xs">
              {isMet ? (
                <Check className="w-4 h-4 text-green-600" />
              ) : (
                <X className="w-4 h-4 text-gray-400" />
              )}
              <span className={isMet ? 'text-green-700 font-medium' : 'text-slate-400'}>
                {req.label}
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

/**
 * Validate password strength
 * Returns object with isValid and errors array
 */
export const validatePassword = (password) => {
  const errors = [];
  
  if (password.length < 8) {
    errors.push('Password must be at least 8 characters long');
  }
  
  if (!/[A-Z]/.test(password)) {
    errors.push('Password must contain at least one uppercase letter');
  }
  
  if (!/[a-z]/.test(password)) {
    errors.push('Password must contain at least one lowercase letter');
  }
  
  if (!/\d/.test(password)) {
    errors.push('Password must contain at least one number');
  }
  
  if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
    errors.push('Password must contain at least one special character');
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
};

export default PasswordRequirements;
